import { isEmpty, isEqual, isNil, partition } from 'lodash';
import React, {
  Component,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ScrollView, TextInput } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { Styles } from '@shared/components/form/styles';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { MBEmptyListPlaceholder } from '@shared/components/list/emptyListPlaceholder';
import { MBSelectableDot } from '@shared/components/list/row/selectable';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  MBTextBody1,
  MBTextBody2,
  MBTextBody2Bold,
  MBTextOverlineBold,
} from '@shared/components/typography';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { MBAlert } from '@shared/util/alert';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

const ROW_HEIGHT = grid(7);

function defaultKeyExtractor<T>(value: T): string {
  return String(value);
}

export interface MBMultiSelectProps<T> {
  onSearch?(keyword: string): void;
  onChange?(values: T[]): void;
  onClose?(): void;
  onCreate?(value: string): Promise<unknown>;
  renderItem(value: T, index?: number): ReactNode;
  renderArchivedItem?(value: T): ReactNode;
  renderCreateNew?(query: string, creating: boolean): ReactNode;
  renderEmptyState?(query: string): ReactNode;
  values: T[];
  selectedValues?: T[];
  archivedValues?: T[];
  match?(value: T, query: string, exact: boolean): boolean;
  keyExtractor?(value: T): string;
  labelExtractor?(value: T): string | undefined;
  max?: number;
  selectAll?: boolean;
  selectAllLabel?: string;
  errorLabel?: string;
  renderSelectAll?(): ReactNode;
  itemsEditable?: boolean;
  onEdit?(value: T, component: Component): void;
  onChangeLabel?(value: T, label: string): Promise<void>;
  onArchive?(value: T): Promise<void>;
  onUnarchive?(value: T): Promise<void>;
  editMode?: boolean;
  disableSelection?: boolean;
  itemLabel?: string;
  itemLabelPlural?: string;
  helpText?: string;
  header?: ReactNode;
  displaySelected?: boolean;
  isRequired?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?(): void;
}

type ListItem<T> = { value: T; selected: boolean; archived: boolean };

function alwaysMatch() {
  return true;
}
function defaultOnClose() {
  return null;
}

export function MBMultiSelect<T>({
  onSearch,
  onChange,
  onCreate,
  onEdit,
  renderItem,
  renderArchivedItem,
  renderCreateNew,
  renderEmptyState,
  values,
  selectedValues,
  match = alwaysMatch,
  keyExtractor = defaultKeyExtractor,
  max = 0,
  selectAll = false,
  selectAllLabel = i18n('common.selectAll'),
  errorLabel = i18n('errors.card.enterName'),
  renderSelectAll,
  onClose = defaultOnClose,
  editMode,
  archivedValues,
  helpText,
  header,
  disableSelection,
  displaySelected,
  isRequired = false,
  loading = false,
  hasMore = false,
  loadMore,
}: MBMultiSelectProps<T>): ReactElement {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const [query, _setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const setQuery = useCallback(
    (keyword: string) => {
      _setQuery(keyword);
      onSearch?.(keyword);
    },
    [onSearch],
  );
  const filteredItems = useMemo<ListItem<T>[]>(() => {
    return (values ?? [])
      .filter(value => match(value, query, false))
      .map(item => ({
        value: item,
        selected: !!selectedValues?.some(
          selectedItem => keyExtractor(selectedItem) === keyExtractor(item),
        ),
        archived: !!archivedValues?.some(
          archivedItem => keyExtractor(archivedItem) === keyExtractor(item),
        ),
      }));
  }, [values, match, query, selectedValues, archivedValues, keyExtractor]);

  const nonArchivedValues = useMemo(
    () =>
      values?.filter(
        item =>
          !archivedValues?.some(archivedItem => keyExtractor(archivedItem) === keyExtractor(item)),
      ),
    [archivedValues, keyExtractor, values],
  );

  const isAllSelected = useMemo(() => {
    return nonArchivedValues.length > 0 && nonArchivedValues.length === selectedValues?.length;
  }, [nonArchivedValues, selectedValues]);

  const toggleItem = useCallback(
    (value: T, component: Component) => {
      const selected = selectedValues ?? [];
      if (editMode) {
        onEdit?.(value, component);
        return;
      }
      if (max === 1) {
        if (!isRequired) {
          const newValue = isEqual(selected, [value]) ? [] : [value];
          onChange?.(newValue);
        } else {
          onChange?.([value]);
        }
        onClose();
      } else if (selected.find(v => keyExtractor(v) === keyExtractor(value))) {
        if (!isRequired || selected.length > 1) {
          onChange?.(selected.filter(v => keyExtractor(v) !== keyExtractor(value)));
        }
      } else if (max === 0 || selected.length < max) {
        onChange?.([...selected, value]);
      }
    },
    [selectedValues, max, keyExtractor, onChange, onClose, editMode, onEdit, isRequired],
  );

  const toggleSelectAll = useCallback(() => {
    const allSelectableValues = values.filter(value => !archivedValues?.includes(value));
    const newValues = isAllSelected ? [] : allSelectableValues;
    onChange?.(newValues);
  }, [isAllSelected, onChange, values, archivedValues]);

  const renderListItem = useCallback(
    (listItem: ListItem<T>, index: number): ReactNode => (
      <MBHover
        hoverStyle={{
          backgroundColor: Colors.v2.backgroundGrayLight,
        }}
        key={index}
      >
        <MBTouchableOpacity
          key={keyExtractor(listItem.value)}
          onPress={component => toggleItem(listItem.value, component)}
          disabled={(!editMode && listItem.archived) || disableSelection}
          style={{ paddingHorizontal: grid(2) }}
        >
          <View row centerVertically style={{ minHeight: ROW_HEIGHT }}>
            <View flex>
              {listItem.archived
                ? renderArchivedItem?.(listItem.value)
                : renderItem(listItem.value, index)}
            </View>

            {!editMode && (
              <View center style={{ minHeight: ROW_HEIGHT }}>
                <MBSelectableDot
                  radio={max === 1}
                  selected={listItem.selected}
                  testID={getTestId(
                    'card-select-item',
                    index,
                    'checkbox',
                    listItem.selected ? 'checked' : 'unchecked',
                  )}
                />
              </View>
            )}
            {editMode && (
              <View style={{ minHeight: ROW_HEIGHT }} paddingTop={0.5} centerVertically>
                <MBIconV2 name="arrow-point-right" color={Colors.v2.gray30} size={grid(3)} />
              </View>
            )}
          </View>
        </MBTouchableOpacity>
      </MBHover>
    ),
    [keyExtractor, disableSelection, editMode, renderArchivedItem, renderItem, max, toggleItem],
  );

  const searchInputRef = useRef<TextInput>(null);

  const handleCreateNewPress = useCallback(async () => {
    if (query.length === 0) {
      setError(errorLabel);
      searchInputRef.current?.focus();
    } else {
      if (!onCreate) {
        return;
      }
      if (values.find(item => match(item, query, true))) {
        return;
      }
      setCreating(true);
      try {
        await onCreate(query);
        setTimeout(() => {
          setQuery('');
          searchInputRef.current?.focus();
        }, 1000);
      } catch (err) {
        MBAlert.alert({ message: i18n('errors.tag.create') });
        console.error(String(err));
      } finally {
        setCreating(false);
      }
    }
  }, [query, errorLabel, onCreate, values, match, setQuery]);

  const [activeItems, archivedItems] = useMemo(
    () => partition(filteredItems, item => !item.archived),
    [filteredItems],
  );

  const onChangeSearchText = useCallback(
    (updateQuery: string) => {
      setQuery(updateQuery);
      setError(null);
    },
    [setQuery, setError],
  );

  const maxReached = !isNil(max) && max > 0 && (selectedValues?.length ?? 0) >= max ? true : false;
  const searchPlaceholder = editMode ? i18n('common.search') : i18n('common.searchOrCreate');

  return (
    <View fill paddingBottom={1}>
      <MBView style={Styles.whiteBackground} paddingTop={2} paddingBottom={1} paddingHorizontal={2}>
        {header}
        <MBSearchBar
          style={{ paddingLeft: 2 }}
          forwardRef={searchInputRef}
          value={query}
          onChangeText={onChangeSearchText}
          placeholder={onCreate ? searchPlaceholder : undefined}
          returnKeyType="go"
          autoFocus={isDesktop}
        />
        {!!error && (
          <MBTextBody2 color={colors.error.foreground} style={{ padding: grid(1) }}>
            {error}
          </MBTextBody2>
        )}
        {displaySelected && (
          <View paddingTop={2} paddingHorizontal={1} row>
            <MBTextBody2Bold>{selectedValues?.length + ' '}</MBTextBody2Bold>
            <MBTextBody2>
              {!maxReached && i18n('card.blocks.cardsInput.selected.default')}
              {maxReached && i18n('card.blocks.cardsInput.selected.maxReached')}
            </MBTextBody2>
          </View>
        )}
      </MBView>
      <View fill>
        <ScrollView keyboardShouldPersistTaps="always">
          {loading && (
            <View center style={{ height: 150 }}>
              <MBLoadingIndicator />
            </View>
          )}
          {!loading && isEmpty(activeItems) && isNil(renderEmptyState) && (
            <MBEmptyListPlaceholder title={i18n('common.empty')} />
          )}
          {!!helpText && (
            <View paddingVertical={2} paddingHorizontal={1} center>
              <MBTextBody1 style={{ textAlign: 'left' }}>{helpText}</MBTextBody1>
            </View>
          )}
          {selectAll &&
            max !== 1 &&
            query.length === 0 &&
            filteredItems.length > 0 &&
            !editMode && (
              <MBTouchableOpacity onPress={toggleSelectAll}>
                <View row centerVertically paddingHorizontal={2}>
                  {renderSelectAll?.() || (
                    <View flex centerVertically paddingLeft={2} style={{ height: ROW_HEIGHT }}>
                      <MBTextBody1>{selectAllLabel}</MBTextBody1>
                    </View>
                  )}
                  <MBSelectableDot radio={false} selected={isAllSelected} />
                </View>
              </MBTouchableOpacity>
            )}
          {activeItems.map(renderListItem)}
          {activeItems.length === 0 && renderEmptyState?.(query)}
          {archivedItems.length > 0 && editMode && (
            <View paddingLeft={3} paddingVertical={0.5}>
              <MBTextOverlineBold color={Colors.v2.gray50}>
                {i18n('common.archived')}
              </MBTextOverlineBold>
            </View>
          )}
          {editMode && archivedItems.map(renderListItem)}
          {hasMore && (
            <View paddingVertical={3}>
              <MBButton
                title={i18n('card.list.showMore')}
                size="small"
                theme="transparent"
                bold={false}
                borderColor={Colors.v2.gray10}
                onPress={loadMore}
                style={{ alignSelf: 'center' }}
              />
            </View>
          )}
        </ScrollView>
      </View>
      {!!renderCreateNew && !!onCreate && !editMode && (
        <MBTouchableOpacity
          onPress={handleCreateNewPress}
          disabled={creating}
          style={{ paddingHorizontal: grid(2) }}
        >
          {renderCreateNew(query, creating)}
        </MBTouchableOpacity>
      )}
    </View>
  );
}
